/**
 * ErrorBoundary Component
 *
 * Top-level error boundary with auto-recovery for DOM timing errors.
 * Captures errors to Sentry when configured.
 */

import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import * as Sentry from '@sentry/react';
import { breadcrumbs } from '@/lib/breadcrumbs';
import { recoveredErrorTracker } from '@/lib/recoveredErrorTracker';
import { isSentryConfigured } from '@/lib/sentry';
import { ErrorFallback } from './ErrorFallback';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  enableDiagnostics?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId?: string;
  errorTimestamp?: string;
  sentryReported: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryCount = 0;
  private maxRetries = 3;
  private autoRetryCount = 0;
  private maxAutoRetries = 2;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, sentryReported: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      errorTimestamp: new Date().toISOString(),
    };
  }

  /**
   * Check if an error is a DOM/portal timing error that can be auto-recovered
   */
  private isDOMTimingError(error: Error): boolean {
    const message = error.message.toLowerCase();
    return (
      message.includes('removechild') ||
      message.includes('insertbefore') ||
      message.includes('appendchild') ||
      message.includes('parentnode') ||
      (message.includes('failed to execute') && message.includes('node'))
    );
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Check if this is a DOM timing error we can auto-recover from
    if (this.isDOMTimingError(error) && this.autoRetryCount < this.maxAutoRetries) {
      this.autoRetryCount++;
      console.warn(
        `[ErrorBoundary] Auto-recovering from DOM error (attempt ${this.autoRetryCount}/${this.maxAutoRetries}):`,
        error.message
      );

      // Track for telemetry - this is NOT hidden from monitoring
      recoveredErrorTracker.track(error, 'ErrorBoundary', this.autoRetryCount);

      // Send to Sentry with recovered:true tag so we can monitor frequency
      if (isSentryConfigured()) {
        const sentryClient = Sentry.getClient();
        if (sentryClient) {
          Sentry.captureException(error, {
            level: 'warning', // Warning, not error - it was recovered
            tags: {
              source: 'error-boundary',
              recovered: true,
              isDOMError: true,
              autoRetryAttempt: this.autoRetryCount,
            },
            extra: {
              componentStack: errorInfo.componentStack,
              recoveredErrorCount: recoveredErrorTracker.getCount(),
            },
            fingerprint: ['dom-timing-error', error.message.slice(0, 50)],
          });
        }
      }

      // Log to breadcrumbs for local diagnostics
      breadcrumbs.error(`[Auto-recovered] ${error.message}`, {
        autoRetryCount: this.autoRetryCount,
      });

      // Auto-recover after a brief delay
      setTimeout(() => {
        this.setState({
          hasError: false,
          error: undefined,
          errorInfo: undefined,
          sentryReported: false,
        });
      }, 100);

      return; // Don't show error UI
    }

    // Generate errorId if not already set in state
    const errorId =
      this.state.errorId || `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    this.setState({ errorInfo });

    // Send to Sentry
    if (isSentryConfigured()) {
      const sentryClient = Sentry.getClient();
      if (sentryClient) {
        Sentry.captureException(error, {
          tags: { source: 'error-boundary' },
          extra: {
            errorId,
            componentStack: errorInfo.componentStack,
          },
        });
        this.setState({ sentryReported: true });
      }
    }

    // Check if error is offline-related
    const isOfflineError =
      error.message.includes('IndexedDB') ||
      error.message.includes('database') ||
      error.message.includes('network') ||
      !navigator.onLine;

    // Log error for debugging
    if (this.props.enableDiagnostics !== false) {
      console.error('[ErrorBoundary] Error caught:', {
        error,
        errorInfo,
        errorId,
        retryCount: this.retryCount,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        isOffline: !navigator.onLine,
        isOfflineError,
      });
    }

    // Add error to breadcrumbs trail
    breadcrumbs.error(error.message, {
      errorId,
      stack: error.stack?.slice(0, 500),
    });

    // Call custom error handler
    this.props.onError?.(error, errorInfo);

    // Store error info for debugging
    try {
      const errorData = {
        errorId,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        retryCount: this.retryCount,
        isOffline: !navigator.onLine,
        isOfflineError,
      };

      // Store in sessionStorage for debugging
      sessionStorage.setItem(errorId, JSON.stringify(errorData));

      // Keep only last 10 errors
      const errorKeys: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('error_')) {
          errorKeys.push(key);
        }
      }

      if (errorKeys.length > 10) {
        const oldestKey = errorKeys.sort()[0];
        sessionStorage.removeItem(oldestKey);
      }
    } catch (storageError) {
      console.warn('[ErrorBoundary] Failed to store error info:', storageError);
    }
  }

  handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      console.log(`[ErrorBoundary] Retry attempt ${this.retryCount}/${this.maxRetries}`);
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        errorId: undefined,
        sentryReported: false,
      });
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorId, errorTimestamp, sentryReported } = this.state;

      // Check if error is offline-related
      const isOffline = !navigator.onLine;
      const isOfflineError =
        error?.message.includes('IndexedDB') ||
        error?.message.includes('database') ||
        error?.message.includes('network') ||
        isOffline;

      const title = isOffline ? 'Errore modalità offline' : 'Si è verificato un errore';

      const message = isOffline
        ? "L'applicazione è offline e non può accedere ai dati memorizzati."
        : isOfflineError
          ? 'Accesso ai dati o alla rete non riuscito. Potresti essere offline.'
          : "L'applicazione ha riscontrato un errore imprevisto.";

      return (
        <ErrorFallback
          variant="full-page"
          title={title}
          message={message}
          errorId={errorId}
          errorTimestamp={errorTimestamp}
          error={error}
          onRetry={this.retryCount < this.maxRetries ? this.handleRetry : undefined}
          retryCount={this.retryCount}
          maxRetries={this.maxRetries}
          onReload={this.handleReload}
          sentryReported={sentryReported}
        />
      );
    }

    return this.props.children;
  }
}
